import styled from '@emotion/styled';
import type { Chat } from '@trinity/chat';
import { FileText, Image, Package, BarChart2 } from 'react-feather';
import { segmentationBuilderApi } from './segmentation-api';

interface SegmentationExportProps {
  queryId: number;
  queryIdentifier: string;
  value: Chat.QueryState.SegmentationExport;
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  max-width: 750px;
  margin: 0 auto;
`;

const SuccessBanner = styled.div`
  background: linear-gradient(135deg, #4cb847 0%, #3da138 100%);
  color: #ffffff;
  padding: 24px;
  border-radius: 12px;
  text-align: center;
  box-shadow: 0 8px 24px rgba(76, 184, 71, 0.3);
`;

const BannerTitle = styled.div`
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 8px;
`;

const BannerSubtitle = styled.div`
  font-size: 14px;
  opacity: 0.9;
`;

const ExportGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
`;

const ExportCard = styled.button`
  background: #ffffff;
  border: 2px solid #e5e5e5;
  border-radius: 12px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.2s;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  &:hover {
    border-color: #28aae1;
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(40, 170, 225, 0.15);
  }
`;

const ExportIcon = styled.div<{ $color: string }>`
  width: 56px;
  height: 56px;
  border-radius: 12px;
  background: ${(p) => p.$color}10;
  color: ${(p) => p.$color};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ExportLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #323232;
`;

const ExportDesc = styled.div`
  font-size: 12px;
  color: #626262;
`;

const DashboardButton = styled.button`
  width: 100%;
  padding: 16px;
  border-radius: 12px;
  border: 2px solid #28aae1;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  background: #ffffff;
  color: #28aae1;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  &:hover {
    background: #e5f5fc;
    transform: translateY(-1px);
  }
`;

const CompletionMessage = styled.div`
  text-align: center;
  padding: 20px;
  color: #626262;
  font-size: 14px;
`;

export function SegmentationExport({
  queryId,
  queryIdentifier,
  value,
}: SegmentationExportProps) {
  const handleDownload = async (type: 'excel' | 'powerpoint' | 'all') => {
    try {
      const { downloadUrl } = await segmentationBuilderApi.exportResults(queryIdentifier, type);
      window.open(downloadUrl, '_blank');
    } catch (e) {
      console.error('Export failed:', e);
    }
  };

  const handleCreateDashboards = () => {
    console.log('Creating dashboards for session:', value.sessionId);
    // TODO: Implement dashboard creation
  };

  return (
    <Wrapper>
      <SuccessBanner>
        <BannerTitle>✓ Segmentation Complete!</BannerTitle>
        <BannerSubtitle>Your HCP segments are ready for export and analysis</BannerSubtitle>
      </SuccessBanner>

      <div>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#323232', marginBottom: 12 }}>Export Options</div>
        <ExportGrid>
          <ExportCard onClick={() => handleDownload('excel')}>
            <ExportIcon $color="#4cb847">
              <FileText size={24} />
            </ExportIcon>
            <ExportLabel>Excel Export</ExportLabel>
            <ExportDesc>Segmented HCP list with assignments</ExportDesc>
          </ExportCard>

          <ExportCard onClick={() => handleDownload('powerpoint')}>
            <ExportIcon $color="#e12e40">
              <Image size={24} />
            </ExportIcon>
            <ExportLabel>PowerPoint Deck</ExportLabel>
            <ExportDesc>Executive summary with visuals</ExportDesc>
          </ExportCard>

          <ExportCard onClick={() => handleDownload('all')}>
            <ExportIcon $color="#565add">
              <Package size={24} />
            </ExportIcon>
            <ExportLabel>Download All</ExportLabel>
            <ExportDesc>Complete ZIP bundle</ExportDesc>
          </ExportCard>
        </ExportGrid>
      </div>

      <DashboardButton onClick={handleCreateDashboards}>
        <BarChart2 size={18} />
        Create Interactive Dashboards
      </DashboardButton>

      <CompletionMessage>
        Your segmentation has been saved. You can return to this analysis at any time from your projects dashboard.
      </CompletionMessage>
    </Wrapper>
  );
}
